import { FormEvent, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';

import ActionButton from '../../../components/Buttons/ActionButton';
import InputField from '../../../components/Forms/Input/InputField';
import FormModal from '../../../components/Modals/FormModal';
import { RestaurantDiningRoomDTO } from '../../../types/restaurant/RestaurantDiningRoomTypes';
import { RestaurantTableDTO } from '../../../types/restaurant/RestaurantTableTypes';

interface Props {
  /** Salón para el que se crean mesas; null = modal cerrado. */
  room: RestaurantDiningRoomDTO | null;
  onClose: () => void;
  onCreate: (input: {
    diningRoomId: number;
    name: string;
    capacity: number;
  }) => Promise<RestaurantTableDTO | null>;
}

/**
 * Alta rápida de mesas para un salón, desde la propia pantalla de salones.
 * Solo pide nombre y capacidad; el resto (estado, forma, código) usa valores
 * por defecto y puede ajustarse luego en la pantalla de Mesas.
 */
const DiningRoomQuickTableModal = ({ room, onClose, onCreate }: Props) => {
  const [name, setName] = useState('');
  const [capacity, setCapacity] = useState(4);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<string[]>([]);

  useEffect(() => {
    if (room) {
      setName('');
      setCapacity(4);
      setCreated([]);
    }
  }, [room]);

  const nameError =
    name.trim().length > 100 ? 'Máximo 100 caracteres.' : undefined;
  const capacityError =
    capacity < 1 || capacity > 1000 ? 'Debe estar entre 1 y 1000.' : undefined;
  const canSave =
    !!name.trim() && !nameError && !capacityError && !creating;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!room || !canSave) return;
    setCreating(true);
    try {
      const table = await onCreate({
        diningRoomId: room.id,
        name: name.trim(),
        capacity,
      });
      if (table) {
        setCreated((current) => [...current, table.name || name.trim()]);
        setName('');
      }
    } finally {
      setCreating(false);
    }
  };

  return (
    <FormModal
      isOpen={!!room}
      onClose={creating ? undefined : onClose}
      title={room ? `Agregar mesas a “${room.name}”` : 'Agregar mesas'}
      maxWidth="max-w-md"
      zIndex="top"
    >
      <form onSubmit={submit} className="space-y-4">
        <p className="text-sm text-bodydark2">
          Crea mesas para este salón sin salir de la pantalla. Puedes seguir
          agregando y ajustar el resto de datos luego en Mesas.
        </p>

        <InputField
          label="Nombre de la mesa"
          name="quickTableName"
          type="text"
          iconKey="table"
          value={name}
          required
          error={nameError}
          placeholder="Ej. Mesa 1"
          disabled={creating}
          onChange={(event) => setName(event.target.value)}
        />

        <InputField
          label="Capacidad"
          name="quickTableCapacity"
          type="number"
          iconKey="user"
          value={capacity}
          min={1}
          max={1000}
          error={capacityError}
          disabled={creating}
          onChange={(event) => setCapacity(Number(event.target.value))}
        />

        {created.length > 0 && (
          <div className="rounded-lg border border-success/30 bg-success/5 p-3 text-sm text-success">
            Mesas creadas en esta sesión: {created.join(', ')}
          </div>
        )}

        <div className="flex justify-end gap-3 border-t border-stroke pt-4 dark:border-strokedark">
          <ActionButton
            label="Listo"
            variant="secondary"
            size="md"
            fullWidth={false}
            disabled={creating}
            onClick={onClose}
          />
          <button
            type="submit"
            disabled={!canSave}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-medium text-white transition-all duration-200 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {creating && <Loader2 className="h-4 w-4 animate-spin" />}
            {creating ? 'Creando...' : 'Crear mesa'}
          </button>
        </div>
      </form>
    </FormModal>
  );
};

export default DiningRoomQuickTableModal;
